// What the code map shows: a filter over the snapshot, decided here (pure) and
// applied by the renderer as visibility. Nothing moves when it changes: the
// layout is the snapshot's, so a filter only hides and shows (and folds events
// that share their ends into one counted node). The URL carries only what
// differs from the mode's default, so /map?mode=flow means "Data flow, as designed".
//
// Planned by Fable, reviewed by Codex Astra (2026-10-11): kinds and repos as the
// filter, event folding by shared ends as the fix for the event lattice,
// "isolate connections" from the inspector, "both ends" as an opt-in diagnostic.
import type { GraphEdge, GraphNode, GraphSnapshot } from "@/api/types";
import type { MapMode } from "@/components/map/CodeMap.vue";

export type GroupId = "events" | "bus" | "routes" | "aws" | "agents" | "modules" | "symbols";

export const KIND_GROUPS: readonly { id: GroupId; label: string; kinds: readonly string[] }[] = [
  { id: "events", label: "Wire events", kinds: ["contract_event"] },
  { id: "bus", label: "Bus events", kinds: ["bus_event"] },
  { id: "routes", label: "HTTP routes", kinds: ["http_route"] },
  { id: "aws", label: "AWS resources", kinds: ["aws_resource"] },
  { id: "agents", label: "Agents and tools", kinds: ["lg_agent", "lg_tool"] },
  { id: "modules", label: "Modules", kinds: ["module"] },
  { id: "symbols", label: "Symbols", kinds: ["symbol"] },
];
export const ALL_GROUPS: readonly GroupId[] = KIND_GROUPS.map((g) => g.id);
const GROUP_OF: Record<string, GroupId> = Object.fromEntries(KIND_GROUPS.flatMap((g) => g.kinds.map((k) => [k, g.id])));

// Repos and packages are the skeleton: never hidden by kind.
const SKELETON = new Set(["repo", "package"]);
export const DOMAIN = new Set(["contract_event", "bus_event", "aws_resource", "http_route", "lg_agent", "lg_tool"]);
// Every domain edge points from code to the domain node; its kind says which end it is.
const PRODUCER = new Set(["emits", "publishes", "defines_resource", "serves_route"]);
const CONSUMER = new Set(["handles", "subscribes", "uses_resource", "calls_route"]);
// Edges that carry data at runtime (the renderer's FLOW_KINDS).
const FLOW = new Set(["emits", "handles", "publishes", "subscribes", "uses_resource", "calls_route", "serves_route"]);
// Folded when several share exactly the same producers and consumers, at the
// map's level of detail: at Packages, ends are packages (labeeb's 42 bus events
// fold to 6, 47 AWS resources to 16, 27 routes to 8); diving in to Modules
// tells them apart again.
const FOLDABLE: Record<string, string> = {
  contract_event: "wire events", bus_event: "bus events", aws_resource: "AWS resources", http_route: "HTTP routes",
};
const FOLD_MIN = 3;
export type FoldLevel = "repo" | "package" | "module";
/** The fold level for a detail band (0 repos … 3 symbols). */
export const foldLevelFor = (band: number): FoldLevel => (band <= 0 ? "repo" : band === 1 ? "package" : "module");
/** How a folded group joins its ends, and what the inspector calls them. */
export const GROUP_ENDS: Record<string, { produce: string; consume: string; producers: string; consumers: string }> = {
  contract_event: { produce: "emits", consume: "handles", producers: "Sent by", consumers: "Handled by" },
  bus_event: { produce: "publishes", consume: "subscribes", producers: "Published by", consumers: "Subscribers" },
  aws_resource: { produce: "defines_resource", consume: "uses_resource", producers: "Defined in", consumers: "Used by" },
  http_route: { produce: "serves_route", consume: "calls_route", producers: "Served by", consumers: "Called from" },
};
export const GROUP_PREFIX = "group:";

export interface MapFilter {
  kinds: ReadonlySet<GroupId>;
  repos: ReadonlySet<string> | null; // null: every repo
  focus: string | null;              // isolate this node and its connections
  fold: boolean;                     // fold events that share their ends
  bothEnds: boolean;                 // only domain nodes with a producer and a consumer
}

export function defaultFilter(mode: MapMode): MapFilter {
  // Data flow is about what moves; elsewhere the event lattice is noise (one chip brings it back).
  const kinds = mode === "flow" ? ALL_GROUPS : ALL_GROUPS.filter((g) => g !== "events" && g !== "bus");
  return { kinds: new Set(kinds), repos: null, focus: null, fold: true, bothEnds: false };
}

const sameSet = <T,>(a: ReadonlySet<T> | null, b: ReadonlySet<T> | null) =>
  a === b || (!!a && !!b && a.size === b.size && [...a].every((x) => b.has(x)));

/** Facets that differ from the mode's default. */
export function deviations(f: MapFilter, mode: MapMode): number {
  const d = defaultFilter(mode);
  return Number(!sameSet(f.kinds, d.kinds)) + Number(!sameSet(f.repos, d.repos)) + Number(f.focus !== d.focus)
    + Number(f.fold !== d.fold) + Number(f.bothEnds !== d.bothEnds);
}

const one = (v: unknown): string | undefined => (Array.isArray(v) ? v[0] : v) as string | undefined;
const list = (v: string) => v.split(",").map((s) => s.trim()).filter(Boolean);

/** The filter a query names: absent keys take the mode's default; unknown values are dropped. */
export function filterFromQuery(q: Record<string, unknown>, mode: MapMode): MapFilter {
  const f = defaultFilter(mode);
  const kinds = one(q.kinds);
  if (typeof kinds === "string") {
    f.kinds = new Set(kinds === "all" ? ALL_GROUPS : kinds === "none" ? [] : list(kinds).filter((k): k is GroupId => ALL_GROUPS.includes(k as GroupId)));
  }
  const repos = one(q.repos);
  if (typeof repos === "string" && repos) f.repos = new Set(list(repos));
  const focus = one(q.focus);
  if (typeof focus === "string" && focus) f.focus = focus;
  const fold = one(q.fold);
  if (fold === "0" || fold === "1") f.fold = fold === "1";
  if (one(q.ends) === "1") f.bothEnds = true;
  return f;
}

/** Query keys for ``f``: what differs from the mode's default, undefined to clear the rest. */
export function filterToQuery(f: MapFilter, mode: MapMode): Record<string, string | undefined> {
  const d = defaultFilter(mode);
  const kinds = sameSet(f.kinds, d.kinds) ? undefined
    : f.kinds.size === ALL_GROUPS.length ? "all" : f.kinds.size === 0 ? "none" : ALL_GROUPS.filter((g) => f.kinds.has(g)).join(",");
  return {
    kinds,
    repos: f.repos && f.repos.size ? [...f.repos].sort().join(",") : undefined,
    focus: f.focus ?? undefined,
    fold: f.fold === d.fold ? undefined : f.fold ? "1" : "0",
    ends: f.bothEnds ? "1" : undefined,
  };
}

export interface EventGroup {
  id: string;
  kind: string;           // the members' kind
  label: string;          // "42 wire events"
  members: string[];
  producers: string[];    // ids at the producing end (modules, packages or repos, by fold level)
  consumers: string[];
  x: number;
  y: number;
}

export interface FilterView {
  /** Nodes the filter lets through (the zoom level still decides what is drawn). */
  eligible: Set<string>;
  groups: EventGroup[];
  total: number;
  filteredOut: number;    // hidden by the filter
  folded: number;         // drawn inside a group
  byGroup: Record<GroupId, { total: number; shown: number }>;
  byRepo: Record<string, { total: number; shown: number }>;
}

interface Index { byId: Map<string, GraphNode>; incoming: Map<string, GraphEdge[]>; adjacent: Map<string, Set<string>> }

const indexes = new WeakMap<GraphSnapshot, Index>();

function indexOf(s: GraphSnapshot): Index {
  let ix = indexes.get(s);
  if (ix) return ix;
  const byId = new Map(s.nodes.map((n) => [n.id, n]));
  const incoming = new Map<string, GraphEdge[]>();
  const adjacent = new Map<string, Set<string>>();
  for (const e of s.edges) {
    (incoming.get(e.target) ?? incoming.set(e.target, []).get(e.target)!).push(e);
    if (e.kind === "contains") continue;
    (adjacent.get(e.source) ?? adjacent.set(e.source, new Set()).get(e.source)!).add(e.target);
    (adjacent.get(e.target) ?? adjacent.set(e.target, new Set()).get(e.target)!).add(e.source);
  }
  ix = { byId, incoming, adjacent };
  indexes.set(s, ix);
  return ix;
}

function moduleOf(ix: Index, id: string): string {
  let n = ix.byId.get(id);
  for (let i = 0; n && n.kind === "symbol" && n.parent && i < 3; i++) n = ix.byId.get(n.parent);
  return n?.id ?? id;
}

function hash(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

function containerOf(ix: Index, id: string, level: FoldLevel): string {
  if (level === "module") return moduleOf(ix, id);
  let n = ix.byId.get(id);
  for (let i = 0; n && i < 6; i++) {
    if (n.kind === "repo" || (level === "package" && n.kind === "package")) return n.id;
    if (!n.parent || !ix.byId.has(n.parent)) break;
    n = ix.byId.get(n.parent);
  }
  if (level === "repo" && n?.repo && ix.byId.has(`repo:${n.repo}`)) return `repo:${n.repo}`;
  return n?.id ?? id;
}

/** The ends of a domain node: (producers, consumers), as module ids (or packages, repos). */
export function endsOf(s: GraphSnapshot, id: string, level: FoldLevel = "module"): { producers: string[]; consumers: string[] } {
  const ix = indexOf(s);
  const producers = new Set<string>(), consumers = new Set<string>();
  for (const e of ix.incoming.get(id) ?? []) {
    if (PRODUCER.has(e.kind)) producers.add(containerOf(ix, e.source, level));
    else if (CONSUMER.has(e.kind)) consumers.add(containerOf(ix, e.source, level));
  }
  return { producers: [...producers].sort(), consumers: [...consumers].sort() };
}

export function applyFilter(s: GraphSnapshot, f: MapFilter, mode: MapMode, always: Iterable<string> = [],
                            foldAt: FoldLevel = "module"): FilterView {
  const ix = indexOf(s);
  const keep = new Set(always);
  const inRepos = (repo: string | null | undefined) => !f.repos || (!!repo && f.repos.has(repo));
  // A shared domain node (a wire event has no repo) belongs to the repos of the code at its ends.
  const scoped = (n: GraphNode): boolean => {
    if (!DOMAIN.has(n.kind)) return inRepos(n.repo);
    if (!f.repos || inRepos(n.repo)) return true;
    for (const e of ix.incoming.get(n.id) ?? []) if (inRepos(ix.byId.get(e.source)?.repo)) return true;
    return false;
  };
  const kindOn = (n: GraphNode) => SKELETON.has(n.kind) || (GROUP_OF[n.kind] !== undefined && f.kinds.has(GROUP_OF[n.kind]));

  const eligible = new Set<string>();
  const inScope = new Set<string>();
  for (const n of s.nodes) {
    if (!scoped(n)) continue;
    inScope.add(n.id);
    if (!kindOn(n)) continue;
    if (f.bothEnds && DOMAIN.has(n.kind) && n.kind !== "lg_agent" && n.kind !== "lg_tool") {
      const { producers, consumers } = endsOf(s, n.id);
      if (!producers.length || !consumers.length) continue;
    }
    eligible.add(n.id);
  }
  // Data flow: code shows only where it is an end of a flow that is shown.
  if (mode === "flow") {
    const ends = new Set<string>();
    for (const e of s.edges) {
      if (FLOW.has(e.kind) && eligible.has(e.target) && DOMAIN.has(ix.byId.get(e.target)?.kind ?? "")) {
        ends.add(e.source);
        ends.add(moduleOf(ix, e.source));
      }
    }
    for (const id of [...eligible]) {
      const k = ix.byId.get(id)?.kind;
      if ((k === "module" || k === "symbol") && !ends.has(id)) eligible.delete(id);
    }
  }
  // Isolating a node shows it and everything it connects to, whatever the kinds,
  // with the repositories for context.
  const focusSet = new Set<string>();
  if (f.focus && ix.byId.has(f.focus)) {
    focusSet.add(f.focus);
    for (const m of ix.adjacent.get(f.focus) ?? []) focusSet.add(m);
    for (const id of [...eligible]) if (!focusSet.has(id) && ix.byId.get(id)?.kind !== "repo") eligible.delete(id);
    for (const id of focusSet) if (inScope.has(id)) eligible.add(id);
  }
  for (const id of keep) if (ix.byId.has(id)) eligible.add(id);

  // Fold events that share exactly the same producers and consumers.
  const groups: EventGroup[] = [];
  const foldedIds = new Set<string>();
  if (f.fold) {
    const bySig = new Map<string, { kind: string; members: GraphNode[]; producers: string[]; consumers: string[] }>();
    for (const id of eligible) {
      const n = ix.byId.get(id)!;
      if (!FOLDABLE[n.kind] || keep.has(id) || focusSet.has(id)) continue;
      const ends = endsOf(s, id, foldAt);
      const producers = ends.producers.filter((m) => eligible.has(m) || inScope.has(m));
      const consumers = ends.consumers.filter((m) => eligible.has(m) || inScope.has(m));
      const sig = `${n.kind}|${producers.join(",")}|${consumers.join(",")}`;
      const g = bySig.get(sig) ?? { kind: n.kind, members: [], producers, consumers };
      g.members.push(n);
      bySig.set(sig, g);
    }
    for (const [sig, g] of bySig) {
      if (g.members.length < FOLD_MIN) continue;
      const members = g.members.sort((a, b) => a.label.localeCompare(b.label));
      groups.push({
        id: `${GROUP_PREFIX}${g.kind}:${hash(sig)}`, kind: g.kind, label: `${members.length} ${FOLDABLE[g.kind]}`,
        members: members.map((m) => m.id), producers: g.producers, consumers: g.consumers,
        x: members.reduce((a, m) => a + m.x, 0) / members.length, y: members.reduce((a, m) => a + m.y, 0) / members.length,
      });
      for (const m of members) { eligible.delete(m.id); foldedIds.add(m.id); }
    }
    groups.sort((a, b) => b.members.length - a.members.length || a.id.localeCompare(b.id));
  }

  const byGroup = Object.fromEntries(ALL_GROUPS.map((g) => [g, { total: 0, shown: 0 }])) as FilterView["byGroup"];
  const byRepo: FilterView["byRepo"] = {};
  for (const r of s.repos) byRepo[r.repo] = { total: 0, shown: 0 };
  for (const n of s.nodes) {
    const shown = eligible.has(n.id) || foldedIds.has(n.id);
    const g = GROUP_OF[n.kind];
    if (g && inScope.has(n.id)) { byGroup[g].total++; if (shown) byGroup[g].shown++; }
    if (n.repo && byRepo[n.repo]) { byRepo[n.repo].total++; if (shown) byRepo[n.repo].shown++; }
  }
  return {
    eligible, groups, total: s.nodes.length, folded: foldedIds.size,
    filteredOut: s.nodes.length - eligible.size - foldedIds.size,
    byGroup, byRepo,
  };
}

/** A label short enough to share the label grid; the inspector shows the whole name. */
export function shortLabel(label: string, max = 22): string {
  return label.length <= max ? label : `${label.slice(0, max - 1)}…`;
}
